<template>
  <div
    class="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gray-50"
  >
    <!-- Back Button -->
    <div class="absolute top-4 left-4 sm:top-6 sm:left-6">
      <button
        type="button"
        @click="handleBack"
        :disabled="authStore.loading"
        class="flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ArrowLeftIcon class="h-4 w-4 mr-2" aria-hidden="true" />
        {{ t("common.back") }}
      </button>
    </div>

    <!-- Header -->
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <div class="flex justify-center">
        <div class="flex items-center space-x-3">
          <EnvelopeIcon class="h-10 w-10 text-blue-600" />
          <ArrowRightIcon v-if="isEmailBound" class="h-6 w-6 text-gray-400" />
          <CheckCircleIcon
            v-if="isEmailBound"
            class="h-10 w-10 text-green-600"
          />
        </div>
      </div>
      <h2
        class="mt-6 text-center text-2xl/9 font-bold tracking-tight text-gray-900"
      >
        {{ isEmailBound ? t("email.rebindTitle") : t("email.title") }}
      </h2>
      <p class="mt-2 text-center text-sm/6 text-gray-600">
        {{ isEmailBound ? t("email.rebindSubtitle") : t("email.subtitle") }}
      </p>
    </div>

    <!-- Main Form -->
    <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
      <div class="bg-white px-6 py-12 shadow-sm sm:rounded-lg sm:px-12">
        <!-- Current Email Banner -->
        <div
          v-if="isEmailBound"
          class="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg"
        >
          <div class="flex items-center space-x-3">
            <EnvelopeIcon class="h-6 w-6 text-blue-600 flex-shrink-0" />
            <div>
              <h3 class="text-sm font-medium text-blue-800">
                {{ t("email.currentEmail") }}
              </h3>
              <p class="mt-1 text-sm text-blue-700">
                {{ userInfo?.email }}
              </p>
            </div>
          </div>
        </div>

        <!-- Email Form -->
        <form
          @submit.prevent="onFinish(formState)"
          class="space-y-6"
          method="post"
          action="#"
        >
          <!-- 邮箱输入框 -->
          <div>
            <label
              for="email"
              class="block text-sm/6 font-medium text-gray-900"
            >
              {{ t("email.emailAddress") }}
            </label>
            <div class="mt-2">
              <input
                id="email"
                v-model="formState.email"
                name="email"
                type="email"
                autocomplete="email"
                required
                :disabled="authStore.loading"
                class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                :placeholder="t('email.emailPlaceholder')"
              />
            </div>
          </div>

          <!-- 操作按钮 -->
          <div>
            <button
              type="submit"
              :disabled="authStore.loading"
              class="flex w-full justify-center rounded-md bg-blue-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <a-spin v-if="authStore.loading" class="mr-2" size="small" />
              {{
                authStore.loading
                  ? t("email.processing")
                  : isEmailBound
                    ? t("email.rebind")
                    : t("email.confirm")
              }}
            </button>
          </div>

          <!-- Skip Button -->
          <div v-if="!isEmailBound">
            <button
              type="button"
              @click="handleSkip"
              :disabled="authStore.loading"
              class="flex w-full justify-center rounded-md bg-gray-200 px-3 py-1.5 text-sm/6 font-semibold text-gray-700 shadow-xs hover:bg-gray-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ t("email.skip") }}
            </button>
          </div>
        </form>

        <!-- Divider -->
        <div class="relative mt-10">
          <div class="absolute inset-0 flex items-center" aria-hidden="true">
            <div class="w-full border-t border-gray-200" />
          </div>
          <div class="relative flex justify-center text-sm/6 font-medium">
            <span class="bg-white px-6 text-gray-900">{{
              t("email.whySetEmail")
            }}</span>
          </div>
        </div>

        <!-- Help Information -->
        <div class="mt-6">
          <div class="p-4 bg-gray-50 rounded-lg">
            <div class="text-xs text-gray-600 leading-relaxed space-y-4">
              <!-- 邮箱作用说明 -->
              <div>
                <h4 class="font-semibold text-gray-800 mb-2">
                  {{ t("email.reasons.notifications") }}
                </h4>
                <p>{{ t("email.notificationDesc") }}</p>
              </div>

              <!-- 安全保护 -->
              <div>
                <h4 class="font-semibold text-gray-800 mb-2">
                  {{ t("email.reasons.security") }}
                </h4>
                <p>{{ t("email.securityDesc") }}</p>
              </div>

              <!-- 账户保护 -->
              <div>
                <h4 class="font-semibold text-gray-800 mb-2">
                  {{ t("email.reasons.protection") }}
                </h4>
                <p>{{ t("email.protectionDesc") }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import {
  EnvelopeIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
} from "@heroicons/vue/24/outline";
import { useAuthStore } from "@/stores/auth";
import { notificationService } from "@/services/notificationService";
import { reportError } from "@/utils/sentry";
import { jwtService } from "@/services/jwt";

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const authStore = useAuthStore();

const formState = reactive({
  email: "",
});

// 使用 authStore 的响应式用户状态
const userInfo = computed(() => authStore.user);
const isEmailBound = computed(() => !!authStore.user?.email);

// 初始化用户信息（如果 authStore 中没有数据）
async function initializeUserInfo() {
  if (!authStore.user && jwtService.getToken()) {
    try {
      await authStore.fetchUserFromToken();
    } catch (error) {
      reportError(error instanceof Error ? error : new Error(String(error)), {
        component: "EmailView",
        action: "initializeUserInfo",
      });
    }
  }
}

// 处理表单提交
async function onFinish(values: any) {
  if (isEmailBound.value) {
    const confirmed = await notificationService.confirm(
      "email.rebindConfirmTitle",
      "email.rebindConfirmContent",
    );
    if (confirmed) {
      handleRebindConfirm();
    }
    return;
  }
  await updateEmail(values);
}

// 更新邮箱
async function updateEmail(values: any) {
  try {
    await authStore.updateEmail(values.email);
    notificationService.success("email.setSuccess");
    handleRedirect();
  } catch (error: any) {
    reportError(error instanceof Error ? error : new Error(String(error)), {
      component: "EmailView",
      action: "updateEmail",
      email: values.email,
      responseStatus: error.response?.status,
      responseData: error.response?.data,
    });
    const errorMsg = error.response?.data?.message || t("email.setFailed");
    notificationService.error("email.setError", errorMsg);
  }
}

// 处理换绑确认
function handleRebindConfirm() {
  updateEmail(formState);
}

// 处理跳过设置
async function handleSkip() {
  const confirmed = await notificationService.confirm(
    "email.skipConfirmTitle",
    "email.skipConfirmContent",
  );
  if (confirmed) {
    handleRedirect();
  }
}

// 处理重定向
function handleRedirect() {
  const redirect = route.query.redirect as string;
  if (redirect) {
    router.replace(redirect);
  } else {
    router.replace("/authorize");
  }
}

// 返回处理
function handleBack() {
  try {
    // 直接导航到授权页面，避免 router.back() 的问题
    router.push("/authorize");
  } catch (error) {
    console.error("Back navigation error:", error);
    reportError(error as Error, {
      component: "EmailView",
      action: "backNavigation",
    });
    // 回退到授权页面
    router.push("/authorize");
  }
}

onMounted(() => {
  initializeUserInfo();
});
</script>
